/**
 * The New Project wizard's decisions, as pure functions.
 *
 * Step 0 asks WHICH job to copy from, and that answer reaches everything after
 * it: the config the details step prefills, the job number every "carried over
 * from …" sentence names, and whether the request body names a source at all.
 * None of it can be asserted inside the component — this package's Vitest runs
 * in Node with no DOM — so the rules live here and the component stays a
 * rendering of them.
 *
 * What the answer does NOT change is what copying means. Employee rates are
 * still typed by hand, equipment still copies at the source job's current
 * rates under its amber warning, only active rows copy. The source is the one
 * new choice.
 */
import { orderCopySources } from '@coffman-tm/shared';
import type {
  CopySource,
  CopySourceDefaults,
  CopySourceRosterCounts,
  CreateProjectRequest,
  NewProjectCopySelections,
  NewProjectDetails,
  SettingsProject,
} from '@coffman-tm/shared';

/** One row of the Copy from pick list. */
export interface CopySourceOption {
  project_id: string;
  project_number: string;
  name: string;
  /**
   * Null when the row was built locally from the settings project — the
   * fallback after a failed load, which carries no roster counts. A row with
   * no counts prints no counts rather than three zeros that would read as a
   * promise about what copying writes.
   */
  roster_counts: CopySourceRosterCounts | null;
  defaults: CopySourceDefaults;
}

/** Every rate input in the wizard, across step 1 and step 2. */
export interface WizardRateFields {
  st: string;
  swing: string;
  sunday: string;
  /** Stamped onto every transferred employee — step 2's two fields. */
  employeeSt: string;
  employeeSwing: string;
}

/**
 * What a source change does to the rate inputs: clears all five.
 *
 * Not "leaves them alone". Rates never prefill from any job, so a number in
 * one of these fields was typed against a particular source's contract — and
 * both employee fields are written onto every person the copy brings across.
 * The wizard's own gate passes as soon as a field holds a number, so a
 * leftover rate is an invisible wrong bill rather than a visible mistake.
 */
export const BLANK_RATE_FIELDS: WizardRateFields = {
  st: '',
  swing: '',
  sunday: '',
  employeeSt: '',
  employeeSwing: '',
};

/** The job the admin is standing in, as a pick-list row. */
function currentAsOption(current: SettingsProject): CopySourceOption {
  return {
    project_id: current.id,
    project_number: current.project_number,
    name: current.name,
    roster_counts: null,
    defaults: {
      gc_name: current.gc_name,
      gc_email: current.gc_email,
      pm_name: current.pm_name,
      pm_email: current.pm_email,
      markup_pct: current.markup_pct,
      equipment_tax_pct: current.equipment_tax_pct,
      per_diem_rate_cents: current.per_diem_rate_cents,
      default_per_diem_eligible: current.default_per_diem_eligible,
      calculate_ot_dt: current.calculate_ot_dt,
      sunday_premium_enabled: current.sunday_premium_enabled,
    },
  };
}

/**
 * The pick list, from whatever the server answered.
 *
 * `null` sources means the list is in flight or the fetch failed; either way
 * the current job alone is offered, because a wizard with no source to pick is
 * a wizard nobody can use and the create's own fallback is exactly that job.
 *
 * The current job is always present for the same reason — if the server left
 * it out (a membership revoked between page load and now) the default pick
 * would otherwise be a row that no longer exists. The server's own row for it
 * wins when there is one, since that row carries the roster counts.
 */
export function copySourceList(
  sources: CopySource[] | null,
  current: SettingsProject,
): [CopySourceOption, ...CopySourceOption[]] {
  const fromServer: CopySourceOption[] = (sources ?? []).map((s) => ({
    project_id: s.project_id,
    project_number: s.project_number,
    name: s.name,
    roster_counts: s.roster_counts,
    defaults: s.defaults,
  }));
  const all = fromServer.some((o) => o.project_id === current.id)
    ? fromServer
    : [...fromServer, currentAsOption(current)];
  // Never empty — the current job is in `all` either way — and typed that way
  // so no caller has to null-check the default pick.
  return orderCopySources(all, current.id) as [CopySourceOption, ...CopySourceOption[]];
}

/** The picked row, or null once the pick has gone stale. */
export function findCopySource(
  list: CopySourceOption[],
  sourceProjectId: string,
): CopySourceOption | null {
  return list.find((o) => o.project_id === sourceProjectId) ?? null;
}

/** The job number every `{sourceNumber}` sentence names. */
export function copySourceNumber(
  source: CopySourceOption | null,
  current: SettingsProject,
): string {
  return source?.project_number ?? current.project_number;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The muted line under a row's number and name. Null prints nothing. */
export function copySourceRowMeta(source: CopySourceOption): string | null {
  const counts = source.roster_counts;
  if (counts === null) return null;
  if (counts.employees === 0 && counts.equipment === 0 && counts.materials === 0) {
    return 'No roster yet';
  }
  return [
    plural(counts.employees, 'employee', 'employees'),
    plural(counts.equipment, 'equipment', 'equipment'),
    plural(counts.materials, 'material', 'materials'),
  ].join(' · ');
}

export interface WizardPrefill {
  /** Changes only when the ANSWER does — the seeding effect's whole trigger. */
  key: string;
  values: CopySourceDefaults;
  /** Always blank. Named rather than implied, so the rule is one import away. */
  rates: WizardRateFields;
}

/**
 * The config the details step seeds from, plus a key that changes only when
 * the picked source changes — so walking back into step 1 never overwrites a
 * half-typed form, while picking a different job does re-seed it.
 */
export function resolvePrefill(source: CopySourceOption): WizardPrefill {
  return { key: source.project_id, values: source.defaults, rates: BLANK_RATE_FIELDS };
}

/**
 * The create body. The source is OMITTED when the pick is the job the admin is
 * standing in, so that case sends exactly what it sent before the picker
 * existed and lands on the server branch that has been in production.
 */
export function buildCreateProjectRequest(args: {
  details: NewProjectDetails;
  copy: NewProjectCopySelections;
  sourceProjectId: string;
  currentProjectId: string;
}): CreateProjectRequest {
  const { details, copy, sourceProjectId, currentProjectId } = args;
  return {
    details,
    copy,
    ...(sourceProjectId === currentProjectId ? {} : { source_project_id: sourceProjectId }),
  };
}

/**
 * The two ways a named source can be refused, as sentences — and null for
 * everything else, so the caller's form-level mapping still owns the rest.
 *
 * Both send the admin back to step 0, and they are worded apart because the
 * fix is: pick another job, versus ask why your access went away.
 */
export function copySourcePickMessage(code: unknown): string | null {
  if (code === 'source_forbidden') {
    return 'You no longer have access to the job you picked to copy from. Pick another one.';
  }
  if (code === 'source_not_found') {
    return 'The job you picked to copy from is no longer there. Pick another one.';
  }
  return null;
}
